import { ReactNode, useEffect } from "react";
import Sidebar from "./Sidebar";
import OutageBanner from "./OutageBanner";
import { trpc } from "../lib/trpc";
import UserCard from "./UserCard";

export default function DashboardLayout({ children }: { children: ReactNode }) {
  const { data: me, isLoading } = trpc.auth.me.useQuery(undefined, { retry: false });
  const isAdmin = me?.authenticated && (me.role === "admin" || me.role === "master_admin");
  const isMaster = me?.authenticated && me.role === "master_admin";

  // Route-guard: if the auth query finishes and the user is NOT authenticated,
  // bounce them to /login. We allow a bootstrap-token user (no email/role check)
  // through because that's the admin override path.
  useEffect(() => {
    if (isLoading) return;
    if (!me) return; // still resolving (shouldn't happen after isLoading=false)
    if (me.authenticated === false) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?next=${next}`;
    }
  }, [isLoading, me]);

  // Show a thin loader instead of flashing the dashboard before redirect
  if (isLoading || (me && me.authenticated === false)) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-ink-900">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-ink-900 text-zinc-100 md:flex-row">
      <Sidebar isAdmin={!!isAdmin} isMaster={!!isMaster} />
      <main className="flex-1 overflow-y-auto">
        {me?.authenticated && me.impersonating && (
          <a
            href="/api/auth/stop-impersonate"
            className="block bg-amber-500/90 px-4 py-2 text-center text-sm font-medium text-ink-900 hover:bg-amber-400"
          >
            Viewing as <span className="font-bold">{me.name}</span> · tap to return to {me.realName}
          </a>
        )}
        <OutageBanner />
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">{children}</div>
      </main>
      <UserCard />
    </div>
  );
}
